import { Observable, throwError, of } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, concatMap, delay, retryWhen } from 'rxjs/operators';
import { AppStore } from '../core';
import { Router } from '@angular/router';
import { BciWkModalService } from '@bci/webkitbci';
import { ModalGeneralErrorComponent } from '../components/modal-general-error/modal-general-error.component';
import { ModalNoInternetComponent } from '../components/modal-no-internet/modal-no-internet.component';

export const globalConstants = {
  INTERNAL_SERVER_ERROR: 500,
  BUSINESS_ERROR: 460,
  INTERNAL_SERVER_ERROR_CODE: 'AGDTOTEM00001',
  RUT_FORMAT_ERROR_CODE: 'AGDTOTEM00017',
  RUT_FORMAT_ERROR_MESSAGE: 'Rut inválido',
  PHONE_FORMAT_ERROR_CODE: 'AGDTOTEM00011',
  PHONE_FORMAT_ERROR_MESSAGE: 'Teléfono inválido',
  SERVICE_ERROR_CODE: 'AGDTOTEM00025',
};

@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {
  errorCount = Array<{ url: string; count: number }>();
  authenticationErrorCode = 401;
  offline = false;

  constructor(
    private readonly router: Router,
    private readonly appStore: AppStore,
    private modal: BciWkModalService
  ) {
    window.addEventListener('online', () => this.updateIndicator(true));
    window.addEventListener('offline', () => this.updateIndicator(false));
  }

  checkExcludedOperations(req: HttpRequest<any>): boolean | Error {
    if (this.appStore.env !== null) {
      return (
        this.appStore.env.api.excludeFromRetry.filter(
          (current: { path: string }) => req.url.indexOf(current.path) >= 0
        ).length > 0
      );
    }
    throw new Error('Env is not set');
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const authenticationErrorCode = 401;
    return next.handle(req).pipe(
      // retry strategy is implemented here:
      // if error code is 401, 460 or if is an excluded operation just throw the error again,
      // else retry it for 3 times. In order to count tries, we are using `errorCount` with the url as key.

      retryWhen((errors) =>
        errors.pipe(
          concatMap((error) => {
            if (
              this.appStore.env === null ||
              error.status === authenticationErrorCode ||
              error.status === globalConstants.BUSINESS_ERROR ||
              this.checkExcludedOperations(req)
            ) {
              return throwError(() => error);
            }
            const record = this.getUrlFromRecord(error.url);
            if (record !== undefined) {
              if (record.count >= this.appStore.env.api.maxTries) {
                return throwError(() => error);
              }
              record.count += 1;
            } else {
              this.errorCount.push({
                url: error.url,
                count: 1,
              });
            }
            return of(error);
          }),
          delay(
            this.appStore.env !== null ? this.appStore.env.api.retryDelay : 0
          )
        )
      ),
      // Catch errors if they have 401, 460 or if we already retry them `maxTries` times.
      catchError((error: HttpErrorResponse) => this.handleError(error))
    ) as Observable<HttpEvent<any>>;
  }

  public handleError(error: any): any {
    if (
      error.status === globalConstants.INTERNAL_SERVER_ERROR &&
      error.error?.codigo === globalConstants.INTERNAL_SERVER_ERROR_CODE
    ) {
      return this.modal.open(ModalGeneralErrorComponent);
    }

    if (error.status === 0) {
      return this.modal.open(ModalNoInternetComponent);
    }

    return throwError(() => error);
  }

  private getUrlFromRecord(
    urlp: string
  ): { url: string; count: number } | undefined {
    return this.errorCount.find((current) => current.url === urlp);
  }

  updateIndicator(status: boolean): void {
    this.offline = status;
  }
}
